import { Box, Button, Divider, IconButton, Typography } from '@mui/material';
import { FC } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import { Close } from '@mui/icons-material';
import { COLORS } from 'services/constants/colors';
import { TRateFilterProps } from './types';

/**
 * Picks the rate with a button per known rate instead of typing it. The value is kept under the same
 * `f-<selector>` param the separate filter uses, so the request is built the same way for both.
 */
export const RateFilter: FC<TRateFilterProps> = ({ rates, name = 'Rate', selector = 'rate' }) => {
	const navigate = useNavigate();
	const { pathname, search } = useLocation();
	const navigateParams = queryString.parse(search);

	const paramSelector = `f-${selector}`;
	const paramValue = navigateParams[paramSelector];
	const selectedValue = paramValue === undefined || paramValue === null ? '' : String(paramValue);

	const navigateWithParam = (value: string | undefined) => {
		navigate({
			pathname,
			search: `?${queryString.stringify({
				...navigateParams,
				[paramSelector]: value,
				page: 1,
			})}`,
		});
	};

	/** A second click on the picked rate clears the filter. */
	const handleRateClick = (rate: number) => {
		navigateWithParam(selectedValue === String(rate) ? undefined : String(rate));
	};

	return (
		<Box
			sx={{
				display: 'flex',
				alignItems: 'center',
				gap: 0.5,
				borderRadius: '12px',
				borderWidth: '1px',
				borderStyle: 'solid',
				borderColor: 'primary.main',
				p: '1px 0 1px 4px',
				height: '27px',
			}}
		>
			<Typography
				variant="body1"
				fontWeight={500}
				sx={{ fontSize: '.9rem', color: COLORS.PRIMARY, whiteSpace: 'nowrap' }}
			>
				{name}:
			</Typography>
			<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
				{rates.map((rate) => {
					const selected = selectedValue === String(rate);
					return (
						<Button
							key={rate}
							variant="contained"
							color="primary"
							size="small"
							onClick={() => handleRateClick(rate)}
							sx={{
								height: '20px',
								minWidth: '44px',
								px: 0.75,
								py: 0,
								borderRadius: '10px',
								fontSize: '.8rem',
								fontWeight: 'bold',
								whiteSpace: 'nowrap',
								backgroundColor: selected ? COLORS.SECONDARY : COLORS.PRIMARY,
								color: selected ? COLORS.PRIMARY : 'white',
								'&:hover': {
									backgroundColor: selected ? COLORS.SECONDARY : COLORS.PRIMARY,
								},
							}}
						>
							{rate} %
						</Button>
					);
				})}
			</Box>
			<Divider sx={{ height: 18, m: 0.5 }} orientation="vertical" />
			<IconButton
				color="primary"
				sx={{ p: '0 5px 0 0', m: 0 }}
				aria-label="Clear filter"
				onClick={() => navigateWithParam(undefined)}
				disabled={!selectedValue}
			>
				<Close fontSize="small" sx={{ fontSize: '14px' }} />
			</IconButton>
		</Box>
	);
};
